// Information-architecture analysis of the SKILL.md body.
// Detects code/prose balance, reference linkage, decision logic, validation gates,
// example richness, and frontmatter completeness.

export interface CodeBlock { lang: string | null; lineCount: number; charCount: number }
export interface Heading { level: number; text: string }

export interface ArchitectureMetrics {
  codeBlocks: CodeBlock[];
  totalCodeLines: number;
  proseLines: number;
  codeToProseRatio: number;
  longestCodeBlockLines: number;
  longCodeBlockCount: number;
  headings: Heading[];
  maxHeadingDepth: number;
  hasHeadingHierarchyIssue: boolean;
  refLinks: string[];
  scriptLinks: string[];
  assetLinks: string[];
  decisionMarkers: number;
  validationMarkers: number;
  exampleCount: number;
  callouts: number;
  bulletItems: number;
  numberedItems: number;
  hasCriticalSection: boolean;
  hasGuardrails: boolean;
  duplicateParagraphs: number;
}

const FENCE_RE = /^```([^\n]*)\n([\s\S]*?)^```/gm;

const DECISION_RE = /\b(?:if|else|otherwise|when|unless|switch|case|depending on|in case of)\b/gi;
const VALIDATION_RE = /\b(?:validate|verify|check|ensure|confirm|assert|guard|require(?:d)?|prerequisite)\b/gi;
const EXAMPLE_RE = /\b(?:example|sample|scenario|walkthrough|use case|for instance|for example|e\.g\.)\b/gi;
const CALLOUT_RE = /^(?:>\s+|\*\*(?:note|warning|important|critical|caution|tip|see also)\b|:::?\s*)/gim;
const CRITICAL_RE = /^#{2,4}\s+(?:critical|important|warning|guardrails?|safety|never|do not|don'?t)\b/im;
const GUARDRAILS_RE = /\b(?:never|do not|don'?t|must not|forbidden|prohibited|disallowed)\b/gi;

function extractCodeBlocks(text: string): CodeBlock[] {
  const out: CodeBlock[] = [];
  let m: RegExpExecArray | null;
  while ((m = FENCE_RE.exec(text)) !== null) {
    const lang = m[1].trim() || null;
    const body = m[2];
    out.push({ lang, lineCount: body.split('\n').length, charCount: body.length });
  }
  return out;
}

function extractHeadings(text: string): Heading[] {
  const out: Heading[] = [];
  for (const m of text.matchAll(/^(#{1,6})\s+(.+)$/gm)) {
    out.push({ level: m[1].length, text: m[2].trim() });
  }
  return out;
}

function findLinks(text: string, dir: string): string[] {
  const re = new RegExp(`${dir}/[A-Za-z0-9._/-]+`, 'g');
  return [...new Set(text.match(re) ?? [])];
}

function countMatches(text: string, re: RegExp): number {
  return (text.match(re) ?? []).length;
}

function countDuplicateParagraphs(body: string): number {
  const paras = body
    .replace(/```[\s\S]*?```/g, '')
    .split(/\n{2,}/)
    .map((p) => p.trim().toLowerCase())
    .filter((p) => p.length >= 60);
  const seen = new Map<string, number>();
  for (const p of paras) seen.set(p, (seen.get(p) ?? 0) + 1);
  let dupes = 0;
  for (const n of seen.values()) if (n > 1) dupes += n - 1;
  return dupes;
}

function hasHeadingHierarchyIssue(headings: Heading[]): boolean {
  let prev = 0;
  for (const h of headings) {
    if (prev !== 0 && h.level > prev + 1) return true;
    prev = h.level;
  }
  return false;
}

export function analyzeArchitecture(body: string): ArchitectureMetrics {
  const codeBlocks = extractCodeBlocks(body);
  const totalCodeLines = codeBlocks.reduce((acc, b) => acc + b.lineCount, 0);
  const totalLines = body.split('\n').length;
  const proseLines = Math.max(0, totalLines - totalCodeLines);

  const headings = extractHeadings(body);
  const maxHeadingDepth = headings.reduce((acc, h) => Math.max(acc, h.level), 0);

  const refLinks = findLinks(body, 'references');
  const scriptLinks = findLinks(body, 'scripts');
  const assetLinks = findLinks(body, 'assets');

  const decisionMarkers = countMatches(body, DECISION_RE);
  const validationMarkers = countMatches(body, VALIDATION_RE);
  const exampleCount = countMatches(body, EXAMPLE_RE);
  const callouts = countMatches(body, CALLOUT_RE);
  const bulletItems = countMatches(body, /^\s*[-*]\s+/gm);
  const numberedItems = countMatches(body, /^\s*\d+\.\s+/gm);

  return {
    codeBlocks,
    totalCodeLines,
    proseLines,
    codeToProseRatio: proseLines === 0 ? 0 : totalCodeLines / proseLines,
    longestCodeBlockLines: codeBlocks.reduce((acc, b) => Math.max(acc, b.lineCount), 0),
    longCodeBlockCount: codeBlocks.filter((b) => b.lineCount >= 15).length,
    headings,
    maxHeadingDepth,
    hasHeadingHierarchyIssue: hasHeadingHierarchyIssue(headings),
    refLinks,
    scriptLinks,
    assetLinks,
    decisionMarkers,
    validationMarkers,
    exampleCount,
    callouts,
    bulletItems,
    numberedItems,
    hasCriticalSection: CRITICAL_RE.test(body),
    hasGuardrails: countMatches(body, GUARDRAILS_RE) > 0,
    duplicateParagraphs: countDuplicateParagraphs(body),
  };
}
